import { useMemo, useLayoutEffect, useRef, useContext, useEffect, useState } from 'react'
import { AuthenticationProvider, AuthenticationContext, withOidcSecure } from '@axa-fr/react-oidc-context'
import { AuthenticatingTimeout, NotAuthenticated, NotAuthorized, SessionLost, FailedToRenew, InactivityLogout } from '../common/FullscreenError'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { clearStoredExpiredTsgTokens, getAndSaveInitTsgId } from '../../framework/tsg'
import { history } from '../../framework/history'
import { setValuesInternal } from '../../framework/actions'
import { SPARKY_FRAMEWORK, DEFAULT_TSG_OIDC_CONFIG, OIDC_STORE_PREFIX } from '../../framework/constants'
import { alertAndDisableTiming } from '../common/utils'
import { isInSilentLoginIframe } from '../../init'
import { unsetSession } from '../mainOneappNew/fawkesUtils'
import { WebStorageStateStore, type UserManagerSettings } from 'oidc-client'
import type { VFC, ReactElement, ComponentType } from 'react'
import type { FrameworkVars, OidcState } from '../../framework/stateTypes'
import type { SparkySettings } from '../../framework/microAppTypes'

export { withOidcSecure, AuthenticationContext }

const NullComponent: ComponentType = () => null

const OidcLoading: VFC<{}> = () => {
  const TIMEOUT = 10000 // 10s
  const [loading, setLoading] = useState<boolean>(true)
  useEffect(() => {
    const ts = setTimeout(() => {
      setLoading(false)
    }, TIMEOUT)
    return () => {
      clearTimeout(ts)
    }
  })
  return loading ? <LoadingPlaceholder /> : <AuthenticatingTimeout />
}

export const OidcAuthReceiver: VFC<{ children: ReactElement | null, trySilent?: boolean }> = ({ children, trySilent }) => {
  const oidc = useContext(AuthenticationContext)
  const statusRef = useRef(0) // init
  const oidcRef = useRef(oidc)
  oidcRef.current = oidc
  useEffect(() => { // avoid try login before isLoading become true
    const { oidcUser, userManager, login, isLoading, isLoggingOut } = oidc
    if (oidc.isLoading && !statusRef.current) {
      statusRef.current = 1 // loading
    }
    else if ((!oidcUser || oidcUser.expired) && statusRef.current === 1 && login && !isLoggingOut && !isLoading) {
      if (!trySilent) {
        login()
        return
      }
      const unsetFawkesTidAndLogin = () => { // fallback to login if failed
        unsetSession() // ensure previous tenant id been cleared
        clearStoredExpiredTsgTokens() // clear expired tokens in session, avoid invalid grant error
        if (window.location.search?.includes('tenantId')) {
          alertAndDisableTiming('Specify tenantId in url is not supported in TSG based env, please use tsg_id instead')
          const url = new URL(window.location.href)
          url.searchParams.delete('tenantId')
          history.replace(`${url.pathname}${url.search.length > 1 ? url.search : ''}`)
        }
        login() // do not clear session storage here, will reuse stored info without redirect
      }
      try {
        statusRef.current = 2 // login
        userManager.signinSilent().catch(unsetFawkesTidAndLogin)
      }
      catch (e) { // just in case
        unsetFawkesTidAndLogin()
      }
    }
  }, [oidc, trySilent])
  useLayoutEffect(() => {
    const { oidcUser, userManager, login, logout } = oidcRef.current
    // process.env.NODE_ENV === 'development' && console.info('oidc', oidcRef.current)
    if (oidcUser && !oidcUser.expired && !isInSilentLoginIframe) {
      setValuesInternal({
        oidc: Object.defineProperties({
          oidcUser,
        }, { // skip json.stringify
          login: {
            value: login,
          },
          logout: {
            value: logout,
          },
          userManager: {
            value: userManager,
          }
        }) as OidcState
      })
      statusRef.current = 3 // loaded
      if (sessionStorage.reloadRetry) {
        delete sessionStorage.reloadRetry
      }
    }
  }, [oidc.oidcUser])

  const { inactivityLogout, inactivityLogoutMessage } = oidc.userManager as any
  if (inactivityLogout) {
    return <InactivityLogout message={inactivityLogoutMessage} />
  }
  if (oidc.oidcUser) {
    return children
  }
  if (oidc.isLoading) {
    return <OidcLoading />
  }
  if (statusRef.current > 2 && !oidc.isLoggingOut) { // lost oidc
    return <FailedToRenew />
  }
  return <OidcLoading />
}

export const OidcAuthProvider: VFC<{ children: ReactElement | null, settings: SparkySettings }> = ({ children, settings }) => {
  // useLayoutEffect(() => process.env.NODE_ENV === 'development' && configuration && console.info('oidc configuration', configuration), [configuration])
  const oidcConfig = useMemo(() => {
    const { oidc_client: varsConfig, oidc_client_id, private_site_origin } = settings.variables[SPARKY_FRAMEWORK] as FrameworkVars
    const client_id = oidc_client_id || varsConfig?.client_id
    if (!client_id || !varsConfig) {
      return
    }
    const tsgId = getAndSaveInitTsgId()
    const {
      config_url,
      authority = config_url,
      redirect_uri = `${window.origin}/authentication/callback`,
      response_type = 'code',
      scope: defaultScope = 'openid profile email',
      silent_redirect_uri = `${window.origin}${DEFAULT_TSG_OIDC_CONFIG.silent_redirect_uri}`,
      post_logout_redirect_uri,
      automaticSilentRenew = DEFAULT_TSG_OIDC_CONFIG.automaticSilentRenew,
      loadUserInfo = true,
    } = varsConfig
    const scope = tsgId ? `${defaultScope} tsg_id:${tsgId}` : defaultScope
    const store = new WebStorageStateStore({ prefix: OIDC_STORE_PREFIX, store: sessionStorage })
    const config = {
      authority,
      client_id,
      redirect_uri,
      response_type,
      scope,
      silent_redirect_uri,
      post_logout_redirect_uri,
      automaticSilentRenew,
      loadUserInfo,
      stateStore: store,
      userStore: store,
      accessTokenExpiringNotificationTime: DEFAULT_TSG_OIDC_CONFIG.accessTokenExpiringNotificationTime,
    }
    Object.defineProperties(config, {
      trySilent: {
        value: !private_site_origin?.endsWith('.pan.local')
      }
    })
    return config as UserManagerSettings & { trySilent: boolean }
  }, [settings])

  if (!oidcConfig) {
    return children
  }
  return <AuthenticationProvider
    configuration={oidcConfig}
    // UserStore={InMemoryWebStorage} // use sessionStorage by default
    notAuthenticated={NotAuthenticated}
    notAuthorized={NotAuthorized}
    sessionLostComponent={SessionLost}
    authenticating={OidcLoading}
    callbackComponentOverride={NullComponent}
  >
    {isInSilentLoginIframe ? 'Signing-in ...' : <OidcAuthReceiver
      trySilent={oidcConfig.trySilent}
    >{children}</OidcAuthReceiver>}
  </AuthenticationProvider>
}

export default OidcAuthProvider
